import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchBanners, setCurrentBanner } from '../store/slices/bannerSlice';
import { fetchFeaturedProducts } from '../store/slices/productsSlice';
import { fetchCategories } from '../store/slices/categoriesSlice';
import ProductCard from '../components/products/ProductCard';
import CategoryCarousel from '../components/categories/CategoryCarousel';
import HomeReviews from '../components/reviews/HomeReviews';
import HomeBlogs from '../components/blog/HomeBlogs';
import SEO from '../components/SEO/SEO';
import { generateOrganizationSchema, generateWebsiteSchema } from '../utils/structuredData';
import { fetchSEO } from '../store/slices/seoSlice';
import { FaWhatsapp } from 'react-icons/fa';

const HomePage = () => {
  const dispatch = useDispatch();
  const { items: banners, currentBanner, loading: bannerLoading } = useSelector((state) => state.banner);
  const { featuredItems, loading: productsLoading } = useSelector((state) => state.products);
  const { items: categories, loading: categoriesLoading } = useSelector((state) => state.categories);
  const settings = useSelector((state) => state.settings.data);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  useEffect(() => {
    dispatch(fetchBanners('home'));
    dispatch(fetchFeaturedProducts());
    dispatch(fetchCategories());
    dispatch(fetchSEO('home'));
  }, [dispatch]);

  // Update current banner index when banners or currentBanner changes
  useEffect(() => {
    if (banners.length > 0 && currentBanner) {
      const index = banners.findIndex(b => b._id === currentBanner._id);
      if (index !== -1) {
        setCurrentBannerIndex(index);
      }
    }
  }, [banners, currentBanner]);

  // Filter active banners
  const activeBanners = banners.filter(b => b.isActive !== false);
  
  // Get current banner to display
  const banner = activeBanners[currentBannerIndex] || activeBanners[0] || banners[0];

  // Handle dot navigation
  const handleDotClick = (index) => {
    setCurrentBannerIndex(index);
    if (activeBanners[index]) {
      dispatch(setCurrentBanner(activeBanners[index]));
    }
  };

  // Auto-rotate banners every 5 seconds (pauses on hover)
  const [isPaused, setIsPaused] = useState(false);
  
  useEffect(() => {
    if (activeBanners.length > 1 && !isPaused) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prev) => {
          const next = (prev + 1) % activeBanners.length;
          dispatch(setCurrentBanner(activeBanners[next]));
          return next;
        });
      }, 3000); // Change banner every 3 seconds

      return () => clearInterval(interval);
    }
  }, [activeBanners, dispatch, isPaused]);

  const structuredData = [
    generateOrganizationSchema(settings),
    generateWebsiteSchema(settings),
  ];

  return (
    <>
      <SEO
        page="home"
        title="Premium Art Frames & Spiritual Decor"
        description="Premium handcrafted frames and spiritual decor that transform your space into a sanctuary of beauty and positive energy. Shop Vastu art, spiritual frames, and custom designs."
        keywords="art frames, spiritual decor, vastu art, home decor, handcrafted frames, spiritual art, custom frames, India art, premium frames"
        image={banner?.image}
        url="/"
        structuredData={structuredData}
      />
      <div>
        {/* Hero Banner with Carousel - Luxe Style */}
      <section 
        className="relative min-h-[90vh] flex items-center overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {bannerLoading ? (
          <div className="w-full h-full bg-gray-200 animate-pulse" />
        ) : banner && banner.image ? (
          <>
            <img
              src={banner.image}
              alt={banner.title.replace(/<[^>]*>/g, '') || 'Banner'}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-24 z-10">
              <div className="max-w-2xl">
                <p className="text-xs font-sans font-medium uppercase tracking-[0.3em] text-white/80 mb-6">
                  Handcrafted Excellence
                </p>
                <h1 
                  className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[1.1] text-white mb-8"
                  dangerouslySetInnerHTML={{ __html: banner.title }}
                  style={{ 
                    wordBreak: 'break-word'
                  }}
                />
                {banner.description && (
                  <div 
                    className="mt-8 text-lg text-white/90 leading-relaxed max-w-lg"
                    dangerouslySetInnerHTML={{ __html: banner.description }}
                    style={{ 
                      wordBreak: 'break-word'
                    }}
                  />
                )}
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/products"
                    className="bg-primary text-white px-10 py-4 rounded-lg hover:bg-primary-dark transition-all transform hover:scale-105 shadow-2xl font-semibold text-lg uppercase tracking-wider"
                  >
                    Explore Collection
                  </Link>
                  {banner.link && (
                    <a
                      href={banner.link}
                      className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg hover:bg-white hover:text-primary transition-all transform hover:scale-105 font-semibold text-lg uppercase tracking-wider"
                    >
                      Learn More
                    </a>
                  )}
                </div>
              </div>
            </div>
            
            {/* Dot Navigation Indicators */}
            {activeBanners.length > 1 && (
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3 z-20">
                {activeBanners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${
                      index === currentBannerIndex
                        ? 'bg-white w-10 h-3 shadow-lg'
                        : 'bg-white bg-opacity-60 hover:bg-opacity-80 w-3 h-3'
                    }`}
                    aria-label={`Go to banner ${index + 1}`}
                    style={{
                      minWidth: index === currentBannerIndex ? '2.5rem' : '0.75rem',
                      minHeight: '0.75rem'
                    }}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary via-primary-dark to-primary flex items-center justify-center">
            <div className="text-center text-white px-4 max-w-4xl">
              <h1 className="font-heading text-6xl md:text-7xl lg:text-8xl font-black mb-6 drop-shadow-2xl tracking-tight">
                {settings?.companyName || 'Welcome'}
              </h1>
              <p className="text-xl md:text-2xl lg:text-3xl mb-10 font-semibold drop-shadow-lg leading-relaxed">
                {settings?.joinOurJourney?.description || 'Discover our collection'}
              </p>
              <Link
                to="/products"
                className="bg-secondary text-white px-10 py-4 rounded-lg hover:bg-primary transition-all transform hover:scale-105 shadow-2xl font-bold text-lg uppercase tracking-wider inline-block"
              >
                Explore Collection
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* Features */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: '🎨', title: 'Handcrafted', desc: 'Meticulously handcrafted by skilled artisans' },
              { icon: '✨', title: 'Premium Quality', desc: 'Only the finest materials for lasting beauty' },
              { icon: '🏠', title: 'Customizable Designs', desc: 'Designs aligned with Your Vision' },
              { icon: '🚚', title: 'India-wide Delivery', desc: 'Safe packaging, doorstep delivery' },
            ].map((feature, idx) => (
              <div key={idx} className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
                <div className="text-6xl mb-5">{feature.icon}</div>
                <h3 className="font-heading text-2xl font-bold mb-3 text-black uppercase tracking-wide">{feature.title}</h3>
                <p className="text-gray-700 font-medium leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-5xl md:text-6xl font-black text-black mb-6 uppercase tracking-tight">
              Featured Collection
            </h2>
            <p className="text-gray-700 text-xl font-semibold">Handpicked Premium Products Just For You</p>
            <div className="w-24 h-1 bg-primary mx-auto mt-4"></div>
          </div>
          {productsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                  <div className="w-full h-64 bg-gray-200 rounded mb-4" />
                  <div className="h-4 bg-gray-200 rounded mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : featuredItems.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredItems.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-600">No featured products available</p>
          )}
          <div className="text-center mt-12">
            <Link
              to="/products"
              className="bg-primary text-white px-12 py-4 rounded-lg hover:bg-primary-dark transition-all transform hover:scale-105 shadow-xl font-bold text-lg uppercase tracking-wider inline-block"
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Carousel */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-black text-black mb-4 uppercase tracking-tight">Shop by Category</h2>
            <p className="text-gray-700 text-lg font-semibold">Browse Our Collection</p>
            <div className="w-20 h-1 bg-primary mx-auto mt-3"></div>
          </div>
          {categoriesLoading ? (
            <div className="flex gap-6 overflow-x-auto pb-4 justify-center">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="flex-shrink-0 w-24 h-24 rounded-full bg-gray-200 animate-pulse border-2 border-gray-300" />
              ))}
            </div>
          ) : categories.length > 0 ? (
            <CategoryCarousel categories={categories.slice(0, 6)} />
          ) : (
            <p className="text-center text-gray-600">No categories available</p>
          )}
        </div>
      </section>

      {/* Featured Categories Grid */}
      <section className="py-20 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-5xl md:text-6xl font-black text-black mb-6 uppercase tracking-tight">Featured Categories</h2>
            <p className="text-gray-700 text-xl font-semibold">Find the Perfect Product for Every Space</p>
            <div className="w-24 h-1 bg-primary mx-auto mt-4"></div>
          </div>
          {categoriesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-64 bg-gray-200 rounded animate-pulse" />
              ))}
            </div>
          ) : categories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {categories.slice(0, 3).map((category) => (
                <Link
                  key={category._id}
                  to={`/category/${category._id}`}
                  className="relative h-64 rounded-lg overflow-hidden group"
                >
                  <img
                    src={category.image || 'https://images.unsplash.com/photo-1582538885592-e70a5d7ab3d3?w=600'}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark text-white text-4xl font-bold">' + category.name + '</div>';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent flex items-end">
                    <div className="p-8 text-white w-full">
                      <h3 className="font-heading text-3xl font-black mb-3 uppercase tracking-wide drop-shadow-lg">{category.name}</h3>
                      <p className="text-lg font-bold mb-1">{category.productCount || 0} Products</p>
                      {category.subcategories && category.subcategories.length > 0 && (
                        <p className="text-base mt-2 opacity-95 font-semibold">
                          {category.subcategories.length} Subcategories
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-600">No categories available</p>
          )}
        </div>
      </section>

      {/* Customer Reviews Section */}
      <HomeReviews />

      {/* Latest Blog Posts Section */}
      <HomeBlogs />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="font-heading text-5xl md:text-6xl font-black mb-6 uppercase tracking-tight drop-shadow-2xl">
            Looking for Something?
          </h2>
          <p className="text-2xl md:text-3xl mb-12 font-bold opacity-95 leading-relaxed max-w-3xl mx-auto">
            Let Shop and Get Your Product Delivered to Your Doorstep
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            {settings?.whatsappLink && (
              <a
                href={settings.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary px-12 py-4 rounded-lg hover:bg-secondary hover:text-white transition-all transform hover:scale-105 shadow-2xl font-black text-lg uppercase tracking-wider inline-flex items-center justify-center gap-3"
              >
                <FaWhatsapp className="text-2xl" />
                WhatsApp Us
              </a>
            )}
            <Link
              to="/contact"
              className="bg-transparent border-4 border-white text-white px-12 py-4 rounded-lg hover:bg-white hover:text-primary transition-all transform hover:scale-105 font-black text-lg uppercase tracking-wider shadow-xl inline-block"
            >
              Explore Services
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default HomePage;

